// ONE THREE-WAY CALCULATOR FOR EVERY SCREEN WITH TWO ASSETS AND A RATE.
//
// Operator, 12 Sept 2026: "I want screens everywhere for asset-1, asset-2 &
// rate and 3-way auto calculation, that too dynamic which changes with each
// number I change in the 2nd box." Before this, each screen had its own
// arithmetic: Book trade derived only for fiat-against-coin pairs, Convert
// balance asked "how many AED for 1 INR" (0.044 — nobody quotes it that way)
// and had no box for the amount that comes out, the swap had no rate at all.
//
// Two rules, and every screen gets them from here:
//
// 1. WHICH WAY THE RATE READS is decided by the pair, never by the direction of
//    the trade. The base is the asset a dealer would count (coins before
//    stables, stables before fiat, EUR before AED, AED before INR) and the rate
//    is "quote per base": AED per USDT 3.67, INR per AED 22.7, AED per EUR
//    4.22, USDT per BTC 60,000, BTC per ETH 0.03. Buying or selling reads the
//    same way — that is how the operator already says it aloud.
//
// 2. ANY TWO OF quantity, total and rate FILL THE THIRD, dynamically. The two
//    boxes the operator typed most recently are the facts; the third moves.
//    Amounts outrank the rate: correcting a total to the number the
//    counterparty actually paid re-derives the rate rather than the quantity
//    (the rounding case the operator asked for on 11 Sept). Typing the rate
//    moves the amount typed LATER; the first amount on the screen — a balance
//    being converted, a chain row's coins — is the anchor and stays.
import { foldAsset } from './naming';
import { isFiat } from './workflow';
import { fmtRate, fmtRateExact } from './format';

export type Field = 'base' | 'quote' | 'rate';

export interface TradeMathState {
  /** Quantity in the base asset, as typed or derived. */
  base: string;
  /** Total in the quote asset, as typed or derived. */
  quote: string;
  /** Quote per base. */
  rate: string;
  /** The fields the operator typed, oldest first; at most two. The third is derived. */
  owned: Field[];
  /** A fact a later edit pushed out, kept so that blanking that edit brings it
   *  back: paste a rate, brush a key in the total box, backspace — the pasted
   *  rate must still be there (critic, 12 Sept 2026). */
  demoted?: { field: Field; value: string; by: Field };
}

export const EMPTY_MATH: TradeMathState = { base: '', quote: '', rate: '', owned: [] };

// Earlier in the list = base. Alts before ETH before BTC (ETH/BTC is quoted
// as BTC per ETH), coins before stables, stables before fiat, then the fiats
// in the order the desk quotes them.
const ORDER = ['SOL', 'XLM', 'ETH', 'BTC', 'USDC', 'USDT', 'EUR', 'GBP', 'USD', 'AED', 'INR'];
function rank(asset: string): number {
  const f = foldAsset(asset);
  const i = ORDER.indexOf(f);
  if (i >= 0) return i;
  return isFiat(f) ? ORDER.length : -1;   // an unknown coin leads, an unknown fiat trails
}

/** Which of the two assets is the base (quantity) and which the quote (total).
 *  A tie — the same coin on two rails — keeps the first as base. */
export function orient(a: string, b: string): { base: string; quote: string } {
  return rank(b) < rank(a) ? { base: b, quote: a } : { base: a, quote: b };
}

/** The box this asset belongs to in an oriented pair. The exact code wins
 *  over the folded one, so USDT·TRC against USDT·ERC still has two boxes. */
export function fieldIn(asset: string, pair: { base: string; quote: string }): 'base' | 'quote' {
  if (asset === pair.base) return 'base';
  if (asset === pair.quote) return 'quote';
  return foldAsset(asset) === foldAsset(pair.base) ? 'base' : 'quote';
}
/** The box this asset belongs to, given the two assets on the screen. */
export function fieldOf(asset: string, a: string, b: string): 'base' | 'quote' {
  return fieldIn(asset, orient(a, b));
}

/** "AED per USDT", "INR per AED" — the words next to every rate box. */
export function rateWords(a: string, b: string, disp: (x: string) => string = (x) => foldAsset(x)): string {
  const o = orient(a, b);
  return `${disp(o.quote)} per ${disp(o.base)}`;
}
export function ratePlaceholder(a: string, b: string, disp: (x: string) => string = (x) => foldAsset(x)): string {
  const o = orient(a, b);
  return `how many ${disp(o.quote)} for 1 ${disp(o.base)}`;
}

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}
const FIAT_TAIL = new Set(['EUR', 'GBP', 'USD', 'AED', 'INR']);
export function fmtQty(n: number, asset: string): string {
  if (!Number.isFinite(n)) return '';
  const f = foldAsset(asset);
  return isFiat(f) || FIAT_TAIL.has(f) ? n.toFixed(2) : trimZeros(n.toFixed(6));
}
// THE rate format lives in lib/format.ts (item 70 — one fmtRate for the whole
// console); re-exported here so the calculator's callers keep one import.
export { fmtRate };

/** What a pasted number looks like once the desk's habits are stripped:
 *  "36,720", "1,01,050" (lakh grouping), "36 720", "₹34278", "AED 36720",
 *  "36720 AED", "3.672 " → plain digits and at most one dot. Anything else —
 *  "1e3", "0x10", "3.6x" — is handed back untouched so the box shows what was
 *  pasted and the screen's own validation names it. Operator, 12 Sept 2026:
 *  "even if I directly paste the rates, total should be there". */
const PLAIN = /^\d*\.?\d*$/;
export function canonicalAmount(raw: string): string {
  if (PLAIN.test(raw)) return raw;
  let t = raw.replace(/[\s\u00a0\u2009\u202f'_]/g, '').replace(/[₹$€£¥]|د\.إ|AED|INR|USD|EUR|USDT|USDC|BTC|ETH|SOL/gi, '').replace(/^\+/, '');
  // ONE comma with one or two digits after it and no dot is a European decimal
  // ("3,67" from a EUR counterparty), not a thousands group: "36,720" and
  // "1,01,050" keep their grouping. Pasting "3,67" as 367 would have derived a
  // hundredfold total (reviewer, 12 Sept 2026).
  if (/^\d+,\d{1,2}$/.test(t)) t = t.replace(',', '.');
  t = t.replace(/,/g, '');
  return PLAIN.test(t) ? t : raw;
}

const num = (s: string): number | null => {
  const t = canonicalAmount(s);
  if (t.trim() === '' || t === '.') return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** The one field not owned, if exactly two are. */
export function derivedField(owned: Field[]): Field | null {
  if (owned.length !== 2) return null;
  return (['base', 'quote', 'rate'] as Field[]).find((f) => !owned.includes(f)) ?? null;
}

/** Fill the derived field from the two owned ones. Leaves it alone when either
 *  fact does not parse yet (mid-typing), blanks it on a zero divisor. */
export function derive(s: TradeMathState, assets: { base: string; quote: string }): TradeMathState {
  const d = derivedField(s.owned);
  if (!d) return s;
  const b = num(s.base), q = num(s.quote), r = num(s.rate);
  if (d === 'rate')  return (b && q) ? { ...s, rate: fmtRateExact(q / b) } : s;
  if (d === 'quote') return (b && r) ? { ...s, quote: fmtQty(b * r, assets.quote) } : s;
  /* d === 'base' */ return (q && r) ? { ...s, base: fmtQty(q / r, assets.base) } : s;
}

/** The operator typed `value` into `field`. */
export function edit(s: TradeMathState, field: Field, rawValue: string, assets: { base: string; quote: string }): TradeMathState {
  // A paste with separators or a currency tag lands clean; plain typing is
  // left exactly as typed (a trailing dot mid-way through "3.6" included).
  const value = canonicalAmount(rawValue);
  const next: TradeMathState = { ...s, [field]: value };
  if (value.trim() === '') {
    const wasDerived = derivedField(s.owned);
    next.owned = s.owned.filter((f) => f !== field);
    if (s.demoted && s.demoted.by === field) {
      // The edit that pushed a fact out is being undone: the fact comes back
      // and whatever was derived from the undone edit goes.
      const back = s.demoted.field;
      next[back] = s.demoted.value;
      next.owned = next.owned.filter((f) => f !== back).concat(back);
      delete next.demoted;
      if (wasDerived && wasDerived !== field && wasDerived !== back) next[wasDerived] = '';
      return derive(next, assets);
    }
    // A blanked fact is no longer a fact, and whatever was derived from it is
    // stale — blank that too, so a half-retyped quantity never shows an old
    // total beside it.
    if (wasDerived && wasDerived !== field) next[wasDerived] = '';
    return next;
  }
  let owned = s.owned.filter((f) => f !== field).concat(field);
  if (s.demoted?.field === field) delete next.demoted;   // typed on purpose now
  if (owned.length > 2) {
    let out: Field;
    if (field === 'rate') {
      // Typing the rate moves the amount typed later; the earlier one anchors.
      const anchor = owned.find((f) => f !== 'rate') as Field;
      out = owned.find((f) => f !== 'rate' && f !== anchor) as Field;
      owned = [anchor, 'rate'];
    } else {
      // An amount outranks the rate: the rate re-derives.
      out = 'rate';
      owned = owned.filter((f) => f !== 'rate');
    }
    if (!s.demoted || s.demoted.by !== field) next.demoted = { field: out, value: s[out], by: field };
  }
  next.owned = owned;
  return derive(next, assets);
}

/** A value that arrived with the screen (a chain row's coins, the balance
 *  being converted) counts as typed FIRST — it anchors — however late the
 *  effect that delivers it runs, and delivering the same value twice (a
 *  refetch, a re-render) changes nothing. */
export function preset(s: TradeMathState, field: Field, value: string, assets: { base: string; quote: string }): TradeMathState {
  if (s[field] === value && (value === '' ? !s.owned.includes(field) : s.owned.includes(field))) return s;
  const next = edit(s, field, value, assets);
  if (value.trim() === '') return next;
  delete next.demoted;
  return { ...next, owned: [field, ...next.owned.filter((f) => f !== field)] };
}

/** The pair changed (a rail or asset picked). An amount the operator typed
 *  follows its ASSET to whatever box that asset now sits in — a quantity of
 *  coins does not turn into a total because the fiat side changed. An asset
 *  that left the pair takes its amount with it, a derived amount goes with the
 *  fact it came from, and the rate — a fact about a pair that no longer
 *  exists — is dropped and derives again when both amounts are still there. */
export function repair(s: TradeMathState, from: { base: string; quote: string } | null, to: { base: string; quote: string }): TradeMathState {
  const next: TradeMathState = { ...EMPTY_MATH, owned: [] };
  if (from) {
    for (const f of s.owned) {
      if (f === 'rate') continue;
      const asset = from[f];
      if (!asset) continue;
      const inTo = asset === to.base ? 'base' : asset === to.quote ? 'quote'
        : foldAsset(asset) === foldAsset(to.base) ? 'base' : foldAsset(asset) === foldAsset(to.quote) ? 'quote' : null;
      if (!inTo || !to[inTo]) continue;
      next[inTo] = s[f];
      next.owned.push(inTo);
    }
  }
  return derive(next, to);
}

// ── THE ONE RATE BAND TABLE, KEYED BY THE ORIENTED PAIR (14 Sept 2026) ──────
//
// Until the 13 Sept sweep three screens carried a band keyed by the FIAT alone
// (AED 3.0–4.5, INR 80–130 …), which could only speak about a stablecoin
// against a fiat: 0.044 typed on an AED/INR conversion — the rate upside down
// — drew no words at all (gap x in SWEEP-SCOPE-2026-09-13 §4.3). The rate the
// calculator holds is always "quote per base" of the ORIENTED pair (rule 1 at
// the top of this file), so the band is keyed the same way and one table
// serves Book trade, Convert, Swap and Between parties: fix once, applies
// everywhere. The desk quotes the stablecoin family as one asset (USDC rides
// the USDT line), so the lookup folds USDC onto USDT.
export const BAND: Readonly<Record<string, readonly [number, number]>> = {
  'USDT/AED': [3.0, 4.5],
  'USDT/INR': [80, 130],
  'AED/INR':  [20, 30],
  'EUR/AED':  [3.8, 4.8],
  'USD/AED':  [3.60, 3.75],
  'EUR/USD':  [1.0, 1.3],
  'USD/INR':  [80, 100],
};

/** "USDT/AED", "AED/INR" — the pair as the band table and the operator name it. */
export function pairName(base: string, quote: string): string {
  return `${foldAsset(base)}/${foldAsset(quote)}`;
}

/** The band a quote-per-base rate is expected in, or null when the desk has
 *  no expectation for this pair (BTC/USDT, an unknown coin). */
export function bandFor(base: string, quote: string): readonly [number, number] | null {
  const family = (a: string) => { const f = foldAsset(a); return f === 'USDC' ? 'USDT' : f; };   // dialect-ok: compares the FOLD against one literal
  return BAND[`${family(base)}/${family(quote)}`] ?? null;
}

/** Off-band words, or null when the rate sits inside the band (or no band
 *  applies). Names the figures: "A rate of 0.044 is not an AED/INR rate —
 *  AED/INR runs 20–30. Upside down? 1 ÷ 0.044 = 22.73." The upside-down
 *  sentence only when the reciprocal WOULD fit, which is the slip this table
 *  exists for. */
export function bandWords(base: string, quote: string, rate: number): string | null {
  const band = bandFor(base, quote);
  if (!band || !Number.isFinite(rate) || rate <= 0) return null;
  const [lo, hi] = band;
  if (rate >= lo && rate <= hi) return null;
  const pair = pairName(base, quote);
  const flipped = 1 / rate;
  const upsideDown = flipped >= lo && flipped <= hi ? ` Upside down? 1 ÷ ${fmtRateExact(rate)} = ${trimZeros(flipped.toFixed(4))}.` : '';
  // THE ARTICLE FOLLOWS THE SOUND, NOT THE LETTER (21 Oct 2026 audit: "is not an
  // USDT/AED rate"). USDT and USD start with "you", EUR is "euro" — "a"; AED is
  // "ay-ee-dee" and INR "eye-en-are" — "an". Wording only: no figure here changes.
  const article = /^[AIO]/i.test(pair) ? 'an' : 'a';
  return `A rate of ${fmtRateExact(rate)} is not ${article} ${pair} rate — ${pair} runs ${trimZeros(lo.toFixed(2))}–${trimZeros(hi.toFixed(2))}.${upsideDown}`;
}
